// src/components/CollectionsSharedByMe.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";

const ROLE_LABEL = {
  expert: "Expert / Advisor",
  partner: "Partenaire",
  family_office: "Family Office",
  user: "Utilisateur",
  team: "Team",
  admin: "Admin",
};

function badgeColor(role) {
  if (role === "expert") return "bg-purple-50 text-purple-800 border-purple-200";
  if (role === "partner") return "bg-amber-50 text-amber-800 border-amber-200";
  if (role === "family_office") return "bg-blue-50 text-blue-800 border-blue-200";
  return "bg-gray-50 text-gray-800 border-gray-200";
}

export default function CollectionsSharedByMe({ className = "", onManageAccess, onOpenShareModal, onFindExpert }) {
  const { user } = useAuth();
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [rows, setRows] = useState([]);

  const load = async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      setErr(null);

      // ✅ Relation-centric:
      // on lit les partages "que j’ai donnés" (owner = moi)
      // IMPORTANT: nécessite une vue SQL (voir plus bas) ou une table avec owner_id.
      const { data, error } = await supabase
        .rpc("get_collections_shared_by_me")
        .select(
          `
          collection_id,
          collection_title,
          grantee_id,
          grantee_first_name,
          grantee_last_name,
          grantee_email,
          grantee_role,
          can_view,
          can_edit,
          created_at
        `
        )
        .order("created_at", { ascending: false });

      if (error) throw error;
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("[CollectionsSharedByMe] load failed:", e);
      setErr(e?.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const groups = useMemo(() => {
    // Groupe par grantee (personne) pour une lecture "avec qui je collabore"
    const map = new Map();
    for (const r of rows) {
      const key = r.grantee_id || r.grantee_email || "unknown";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    return Array.from(map.entries()).map(([k, items]) => ({ key: k, items }));
  }, [rows]);

  const totalShares = rows.length;
  const totalPeople = groups.length;

  const openManageAccess = () => {
    if (typeof onManageAccess === "function") return onManageAccess();
    // fallback: stay in-app
    try { navigate("/dashboard"); } catch {}
  };



  return (
    <section className={className}>
      <div className="bg-white border border-gray-100 rounded-2xl p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex-1">
            <h3
              className="text-xl md:text-2xl font-light text-gray-900"
              style={{ fontFamily: "Georgia, serif" }}
            >
              {t("dashboard.sharedByMe.title")}
            </h3>
            <p className="mt-2 text-sm md:text-base text-gray-500 leading-relaxed">
              {t("dashboard.sharedByMe.subtitle")}
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs border border-gray-200 bg-gray-50 text-gray-800">
                {t("dashboard.sharedByMe.stats.people", { count: totalPeople })}
              </span>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs border border-gray-200 bg-gray-50 text-gray-800">
                {t("dashboard.sharedByMe.stats.shares", { count: totalShares })}
              </span>
              <button type="button"
                onClick={openManageAccess}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs border border-gray-200 hover:bg-gray-50 text-gray-900"
              >
                {t("dashboard.sharedByMe.cta.manage")}
                <span aria-hidden>→</span>
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={load}
            className="self-start inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 hover:bg-gray-50 text-sm"
            title={t("dashboard.sharedByMe.cta.refresh")}
          >
            ⟳ {t("dashboard.sharedByMe.cta.refresh")}
          </button>
        </div>

        {err ? (
          <div className="mt-6 p-4 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm">
            ❌ {err}
          </div>
        ) : null}

        {loading ? (
          <div className="mt-8 text-center text-gray-400 text-sm py-10">
            {t("dashboard.sharedByMe.loading")}
          </div>
        ) : null}

        {!loading && !err && rows.length === 0 ? (
          <div className="mt-8 text-center py-10">
            <div className="text-gray-900 text-base font-medium">
              {t("dashboard.sharedByMe.empty.title")}
            </div>
            <div className="mt-2 text-gray-500 text-sm max-w-xl mx-auto">
              {t("dashboard.sharedByMe.empty.desc")}
            </div>
            <div className="mt-6 flex justify-center gap-3">
              <Link
                to="/contacts"
                className="inline-flex items-center justify-center rounded-xl bg-black px-4 py-2 text-sm font-medium text-white hover:bg-gray-900"
              >
                {t("dashboard.sharedByMe.empty.cta")}
              </Link>
            </div>
          </div>
        ) : null}

        {!loading && !err && rows.length > 0 ? (
          <div className="mt-8 space-y-4">
            {groups.map(({ key, items }) => {
              const p = items[0];
              const fullName = [p.grantee_first_name, p.grantee_last_name]
                .filter(Boolean)
                .join(" ")
                .trim();
              const display = fullName || p.grantee_email || t("common.unknown");
              const role = p.grantee_role || "user";

              return (
                <div key={key} className="border border-gray-100 rounded-2xl p-4 md:p-5">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-gray-900 font-medium">{display}</div>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${badgeColor(
                            role
                          )}`}
                        >
                          {ROLE_LABEL[role] || role}
                        </span>
                        <span className="text-xs text-gray-400">
                          {t("dashboard.sharedByMe.withYou")}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        {t("dashboard.sharedByMe.collectionsCount", { count: items.length })}
                      </div>
                    </div>

                    {/* CTA: on évite "voir contenu" ici, c’est relation-centric */}
                    <button
                      type="button"
                      onClick={openManageAccess}
                      className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-4 py-2 text-sm hover:bg-gray-50"
                    >
                      {t("dashboard.sharedByMe.cta.editAccess")}
                      <span className="ml-2">→</span>
                    </button>
                  </div>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {items.map((r) => {
                      const accessLabel = r.can_edit
                        ? t("dashboard.sharedByMe.access.edit")
                        : t("dashboard.sharedByMe.access.view");

                      return (
                        <div
                          key={`${r.collection_id}-${r.grantee_id}`}
                          className="rounded-xl border border-gray-100 p-3 bg-gray-50/40"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {r.collection_title || t("common.untitled")}
                              </div>
                              <div className="mt-1 text-xs text-gray-500">
                                {t("dashboard.sharedByMe.accessLabel", { access: accessLabel })}
                              </div>
                            </div>
                            <span className="text-xs px-2 py-0.5 rounded-full border border-gray-200 bg-white text-gray-800">
                              {accessLabel}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-4 text-xs text-gray-400">
                    {t("dashboard.sharedByMe.hint")}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </section>
  );
}
